import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, clearCart } = useCart();
  const { format, currency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const hasPhysical = items.some((i) => i.type === "physical" || i.type === "bundle");

  const checkout = async () => {
    if (items.length === 0) return;
    // If shipping required, route to full cart page where the form lives
    if (hasPhysical) {
      setIsOpen(false);
      navigate("/cart");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        currency,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      };
      const { data } = await api.post("/checkout/cart", payload);
      openRazorpay(data, {
        onSuccess: (orderId) => { window.location.href = `/payment/success?session_id=${orderId}`; },
        onFailure: (dismissed) => {
          if (!dismissed) toast.error("Payment failed. Please try again.");
          setLoading(false);
        },
      });
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-ink/30 backdrop-blur-sm z-[60] transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
        data-testid="cart-overlay"
      />
      <aside
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-canvas z-[70] shadow-2xl transition-transform duration-500 ease-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between p-6 border-b border-lapis/10">
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} className="text-lapis" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl text-lapis">Your basket</h2>
          </div>
          <button onClick={() => setIsOpen(false)} className="text-ink/60 hover:text-lapis" data-testid="cart-close-btn">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {items.length === 0 ? (
            <div className="text-center py-20" data-testid="cart-empty">
              <div className="font-serif italic text-2xl text-ink/40 mb-3">your basket is light</div>
              <div className="text-sm text-ink/50">Open and waiting.</div>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.product_id} className="flex gap-4" data-testid={`cart-item-${item.slug}`}>
                <img src={item.image} alt={item.title} className="w-20 h-24 object-cover rounded-2xl flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="overline text-[0.6rem] mb-1">{item.type}</div>
                  <div className="font-serif text-lg text-ink leading-tight line-clamp-2">{item.title}</div>
                  <div className="text-lapis font-medium mt-1">{format(item.price, item.price_inr)}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-cream/60 rounded-full">
                      <button
                        onClick={() => updateQty(item.product_id, Math.max(0, item.quantity - 1))}
                        className="w-7 h-7 rounded-full hover:bg-peach/40 text-lapis"
                        data-testid={`cart-decrease-${item.slug}`}
                      >
                        <Minus size={12} className="mx-auto" />
                      </button>
                      <span className="text-sm w-6 text-center font-medium" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQty(item.product_id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full hover:bg-peach/40 text-lapis"
                        data-testid={`cart-increase-${item.slug}`}
                      >
                        <Plus size={12} className="mx-auto" />
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="text-xs uppercase tracking-widest text-ink/40 hover:text-peach-deep"
                      data-testid={`cart-remove-${item.slug}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-lapis/10 p-6 space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="overline">Subtotal</span>
              <span className="font-serif text-2xl text-lapis" data-testid="cart-subtotal">{format(subtotal)}</span>
            </div>
            <button onClick={checkout} disabled={loading} className="btn-primary w-full justify-center" data-testid="cart-checkout-btn">
              {loading ? "Opening checkout…" : hasPhysical ? "Continue to shipping" : "Proceed to checkout"}
            </button>
            <button onClick={clearCart} className="text-xs uppercase tracking-widest text-ink/40 hover:text-peach-deep w-full text-center" data-testid="cart-clear-btn">
              Clear basket
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
